import { CurrencyPipe, NgClass, isPlatformBrowser } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  PLATFORM_ID,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'

import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { addMonths, isBefore, parseISO } from 'date-fns'
import { EMPTY, catchError, filter, finalize, of, startWith, take } from 'rxjs'

import { ArticlesService } from '../../../../api/articles-service/articles.service'
import {
  type ArticleDetailsDto,
  type ArticlePurchaseOptions,
  type PurchaseArticleCustomerInput,
  type UserDto,
} from '../../../../api/responses'
import { InputFieldComponent } from '../../../../components/input-field'
import { SubmitButtonComponent } from '../../../../components/submit-button/submit-button.component'
import { NameValidator, PhoneValidator } from '../../../../misc/validators'
import { AuthService } from '../../../../services/auth/auth.service'
import { withLocalePrefix } from '../../../../shared/providers/locale-aware-router'
import {
  calculateArticlePurchaseTotalAmount,
  calculateArticlePurchaseVatAmount,
} from '../../../../shared/utils/article-purchase-price.utils'
import {
  getCountryFieldOptions,
  getProvinceStateFieldOptions,
} from '../../../../shared/utils/country.utils'
import type { EditableFieldDefinition } from '../../../../types/field-types'
import {
  type ArticlePurchasePrice,
  ContentAccessEnum,
} from '../../../../types/types'

const requiredValidator: ValidatorFn = (control: AbstractControl) =>
  Validators.required(control)
const emailValidator: ValidatorFn = (control: AbstractControl) =>
  Validators.email(control)
const requiredTrueValidator: ValidatorFn = (control: AbstractControl) =>
  Validators.requiredTrue(control)
type PricingOption = 'regular' | 'student'

@Component({
  selector: 'dhp-article-purchase-checkout',
  templateUrl: './article-purchase-checkout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    TranslatePipe,
    CurrencyPipe,
    NgClass,
    InputFieldComponent,
    SubmitButtonComponent,
  ],
})
export class ArticlePurchaseCheckoutComponent {
  private readonly route = inject(ActivatedRoute)
  private readonly router = inject(Router)
  private readonly articlesService = inject(ArticlesService)
  private readonly authService = inject(AuthService)
  private readonly destroyRef = inject(DestroyRef)
  private readonly platformId = inject(PLATFORM_ID)
  private readonly translateService = inject(TranslateService)

  // Interpolated into translated HTML rendered via [innerHTML], which can't
  // go through routerLink/the Router - so the /en prefix has to be applied
  // here explicitly instead of relying on LocaleAwareRouter.
  get termsRoute(): string {
    return withLocalePrefix(this.router.url, '/terms-and-conditions')
  }

  readonly article = signal<ArticleDetailsDto['Article'] | null>(null)
  readonly loading = signal(true)
  readonly submitting = signal(false)
  readonly loadErrorKey = signal<string | null>(null)
  readonly submitErrorKey = signal<string | null>(null)
  readonly hasAppliedDefaultPricingSelection = signal(false)

  readonly purchasePrice = computed<ArticlePurchasePrice | null>(
    () => this.article()?.purchase_price ?? null
  )
  readonly currentUser = computed(() => this.authService.currentUser())
  readonly studentEmailVerifiedAt = computed(() =>
    this.parseDateString(this.currentUser()?.emailVerifiedAt)
  )
  readonly purchaseSubtotal = computed(
    () => this.purchasePrice()?.amount ?? null
  )
  readonly studentPurchaseSubtotal = computed(
    () => this.purchasePrice()?.studentAmount ?? null
  )
  /**
   * Eligibility is decided by the server and carried on the price payload. The
   * student rate is now published to everyone, so `studentAmount` being present
   * no longer implies this user may choose it.
   */
  readonly studentPricingApplies = computed(
    () => this.purchasePrice()?.studentPricingApplies === true
  )
  readonly purchaseVatAmount = computed(() =>
    calculateArticlePurchaseVatAmount(
      this.purchaseSubtotal(),
      this.purchasePrice()?.taxPercentage
    )
  )
  readonly studentPurchaseVatAmount = computed(() =>
    calculateArticlePurchaseVatAmount(
      this.studentPurchaseSubtotal(),
      this.purchasePrice()?.taxPercentage
    )
  )
  readonly purchaseTotal = computed(() =>
    calculateArticlePurchaseTotalAmount(
      this.purchaseSubtotal(),
      this.purchasePrice()?.taxPercentage
    )
  )
  readonly studentPurchaseTotal = computed(() =>
    calculateArticlePurchaseTotalAmount(
      this.studentPurchaseSubtotal(),
      this.purchasePrice()?.taxPercentage
    )
  )
  readonly confirmationFormGroup = new FormGroup({
    pricingType: new FormControl<PricingOption>('regular'),
    confirmStudentEnrollment: new FormControl<boolean>(false),
    acceptTerms: new FormControl<boolean>(false, [requiredTrueValidator]),
  })
  readonly selectedPricingType = toSignal(
    this.confirmationFormGroup.controls.pricingType.valueChanges.pipe(
      startWith(this.confirmationFormGroup.controls.pricingType.value)
    ),
    { initialValue: this.confirmationFormGroup.controls.pricingType.value }
  )
  readonly isStudentPricingSelected = computed(
    () => this.selectedPricingType() === 'student'
  )
  readonly useStudentPricing = computed(
    () => this.isStudentPricingSelected() && this.studentPricingApplies()
  )
  readonly isStudentEmailVerificationExpired = computed(() => {
    const emailVerifiedAt = this.studentEmailVerifiedAt()

    if (!emailVerifiedAt) {
      return true
    }

    return isBefore(emailVerifiedAt, addMonths(new Date(), -6))
  })
  readonly showStudentVerificationMessage = computed(
    () =>
      this.studentPricingApplies() && this.isStudentEmailVerificationExpired()
  )
  readonly effectivePurchaseSubtotal = computed(() =>
    this.useStudentPricing()
      ? this.studentPurchaseSubtotal()
      : this.purchaseSubtotal()
  )
  readonly effectivePurchaseVatAmount = computed(() =>
    this.useStudentPricing()
      ? this.studentPurchaseVatAmount()
      : this.purchaseVatAmount()
  )
  readonly effectivePurchaseTotal = computed(() =>
    this.useStudentPricing()
      ? this.studentPurchaseTotal()
      : this.purchaseTotal()
  )
  readonly requiresAddress = computed(
    () => (this.effectivePurchaseTotal() ?? 0) > 100
  )
  readonly isManagedUser = computed(
    () => this.authService.currentUser()?.managed === true
  )

  readonly customerFormGroup = new FormGroup({
    first_name: new FormControl<string | null>(null, [
      requiredValidator,
      NameValidator,
    ]),
    last_name: new FormControl<string | null>(null, [
      NameValidator,
      requiredValidator,
    ]),
    email: new FormControl<string | null>(null, [emailValidator]),
    phone_number: new FormControl<string | null>(null, [PhoneValidator]),
    street: new FormControl<string | null>(null),
    house_number: new FormControl<string | null>(null),
    addition: new FormControl<string | null>(null),
    city: new FormControl<string | null>(null),
    province: new FormControl<string | null>(null),
    zip_code: new FormControl<string | null>(null),
    country: new FormControl<string | null>(null),
  })

  readonly customerFields: EditableFieldDefinition[] = [
    {
      labelKey: 'pages.account.profile.details.firstName',
      formControlName: 'first_name',
      showRequiredIndicator: true,
      type: 'text',
      placeholderKey: 'common.fieldPlaceholder.firstName',
      errorKeys: {
        required: 'common.fieldValidation.required',
        pattern: 'common.fieldValidation.invalidCharactersError.pattern',
      },
    },
    {
      labelKey: 'pages.account.profile.details.lastName',
      formControlName: 'last_name',
      showRequiredIndicator: true,
      type: 'text',
      placeholderKey: 'common.fieldPlaceholder.lastName',
      errorKeys: {
        required: 'common.fieldValidation.required',
        pattern: 'common.fieldValidation.invalidCharactersError.pattern',
      },
    },
    {
      labelKey: 'pages.account.profile.details.email',
      formControlName: 'email',
      showRequiredIndicator: true,
      type: 'text',
      placeholderKey: 'common.fieldPlaceholder.email',
      errorKeys: {
        required: 'common.fieldValidation.required',
        email: 'common.fieldValidation.email',
      },
    },
    {
      labelKey: 'pages.account.profile.details.phoneNumber',
      formControlName: 'phone_number',
      type: 'text',
      placeholderKey: 'common.fieldPlaceholder.phoneNumber',
      errorKeys: {
        pattern: 'common.fieldValidation.phoneNumberErrors.pattern',
      },
    },
  ]

  readonly countryFieldOptions = getCountryFieldOptions(key =>
    String(this.translateService.instant(key))
  )
  readonly selectedCountry = signal<string | null>(
    this.customerFormGroup.controls.country.value
  )
  readonly provinceStateFieldOptions = computed(() =>
    getProvinceStateFieldOptions(this.selectedCountry())
  )
  readonly purchaseFlowQueryParams = computed(() =>
    this.route.snapshot.queryParamMap.get('accountFlow') === 'update'
      ? { accountFlow: 'update' }
      : {}
  )

  readonly addressFields: EditableFieldDefinition[] = [
    {
      labelKey: 'pages.organization.profile.billingAddress.streetAddress',
      formControlName: 'street',
      type: 'text',
      errorKeys: {
        required: 'common.fieldValidation.required',
      },
    },
    {
      labelKey: 'pages.organization.profile.billingAddress.houseNumber',
      formControlName: 'house_number',
      type: 'text',
      errorKeys: {
        required: 'common.fieldValidation.required',
      },
    },
    {
      labelKey: 'pages.organization.profile.billingAddress.addition',
      formControlName: 'addition',
      type: 'text',
    },
    {
      labelKey: 'pages.organization.profile.billingAddress.city',
      formControlName: 'city',
      type: 'text',
      errorKeys: {
        required: 'common.fieldValidation.required',
      },
    },
    {
      labelKey: 'pages.organization.profile.billingAddress.state',
      formControlName: 'province',
      type: 'text',
    },
    {
      labelKey: 'pages.organization.profile.billingAddress.postalCode',
      formControlName: 'zip_code',
      type: 'text',
      placeholderKey: 'common.fieldPlaceholder.postalCode',
      errorKeys: {
        required: 'common.fieldValidation.required',
      },
    },
    {
      labelKey: 'pages.organization.profile.billingAddress.country',
      formControlName: 'country',
      type: 'select',
      options: this.countryFieldOptions,
      errorKeys: {
        required: 'common.fieldValidation.required',
      },
    },
  ]

  readonly countryAddressField = this.getAddressField('country')
  readonly provinceTextAddressField = this.getAddressField('province')
  readonly provinceAddressField = computed<EditableFieldDefinition>(() => {
    const options = this.provinceStateFieldOptions()

    if (options.length === 0) {
      return this.provinceTextAddressField
    }

    return {
      ...this.provinceTextAddressField,
      type: 'select',
      options,
    }
  })
  readonly cityAddressField = this.getAddressField('city')
  readonly zipCodeAddressField = this.getAddressField('zip_code')
  readonly streetAddressField = this.getAddressField('street')
  readonly houseNumberAddressField = this.getAddressField('house_number')
  readonly additionAddressField = this.getAddressField('addition')

  constructor() {
    effect(() => {
      this.currentUser()
      this.isManagedUser()
      this.applyCustomerValidators()
    })

    effect(() => {
      this.requiresAddress()
      this.applyAddressValidators()
    })

    effect(() => {
      this.selectedPricingType()
      this.ensureValidPricingSelection()
      this.applyStudentConfirmationValidators()
    })

    this.patchCustomerFormWithUser(this.authService.currentUser())
    this.authService.currentUser$
      .pipe(
        filter((user): user is UserDto => !!user),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(user => {
        this.patchCustomerFormWithUser(user)
      })

    this.customerFormGroup.controls.country.valueChanges
      .pipe(
        startWith(this.customerFormGroup.controls.country.value),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(country => {
        this.syncProvinceStateField(country)
      })

    this.loadArticle()
  }

  submit(): void {
    if (this.customerFormGroup.invalid || this.confirmationFormGroup.invalid) {
      this.customerFormGroup.markAllAsTouched()
      this.confirmationFormGroup.markAllAsTouched()
      return
    }

    if (this.submitting()) {
      return
    }

    const article = this.article()
    if (!article || !this.purchaseTotal()) {
      this.submitErrorKey.set('pages.articlePurchase.errors.unavailable')
      return
    }

    const customer = this.buildCustomerInput()
    const options = this.buildPurchaseOptions()

    this.submitting.set(true)
    this.submitErrorKey.set(null)

    this.articlesService
      .purchaseArticle(article.id, customer, options)
      .pipe(
        catchError((error: unknown) => {
          console.error('Error starting article purchase', error)
          this.submitErrorKey.set('pages.articlePurchase.errors.submit')
          return EMPTY
        }),
        finalize(() => {
          this.submitting.set(false)
        })
      )
      .subscribe(purchase => {
        if (purchase.checkoutUrl) {
          if (isPlatformBrowser(this.platformId)) {
            window.location.assign(purchase.checkoutUrl)
            return
          }

          void this.router.navigate(
            ['/articles', article.id, 'purchase', 'success'],
            {
              queryParams: {
                ...this.purchaseFlowQueryParams(),
                purchaseId: purchase.purchaseId ?? undefined,
              },
            }
          )
          return
        }

        if (
          this.useStudentPricing() &&
          this.isStudentEmailVerificationExpired()
        ) {
          void this.router.navigate(
            ['/articles', article.id, 'purchase', 'continue-in-email'],
            {
              queryParams: this.purchaseFlowQueryParams(),
              state: {
                email: this.currentUser()?.email ?? customer.email ?? undefined,
              },
            }
          )
          return
        }

        if (!purchase.checkoutUrl) {
          this.submitErrorKey.set('pages.articlePurchase.errors.submit')
        }
      })
  }

  private loadArticle(): void {
    const articleId = parseInt(this.route.snapshot.paramMap.get('id') ?? '', 10)
    if (isNaN(articleId)) {
      this.loadErrorKey.set('pages.articlePurchase.errors.load')
      this.loading.set(false)
      return
    }

    this.articlesService
      .getArticleDetails(articleId, 'network-only')
      .pipe(
        take(1),
        catchError((error: unknown) => {
          console.error('Error loading purchasable article', error)
          this.loadErrorKey.set('pages.articlePurchase.errors.load')
          this.loading.set(false)
          return of(undefined)
        })
      )
      .subscribe(article => {
        if (!article) {
          this.loading.set(false)
          return
        }

        if (
          article.user_access === ContentAccessEnum.SEAT_ASSIGNED ||
          article.user_access === ContentAccessEnum.ARTICLE_PURCHASED
        ) {
          void this.router.navigate(['/articles', article.id])
          return
        }

        this.article.set(article)

        if (!this.purchaseTotal()) {
          this.loadErrorKey.set('pages.articlePurchase.errors.unavailable')
        }

        this.loading.set(false)
      })
  }

  private applyAddressValidators(): void {
    const requiredControls = [
      'street',
      'house_number',
      'city',
      'zip_code',
      'country',
    ]

    for (const controlName of requiredControls) {
      const control = this.customerFormGroup.get(controlName)
      if (!control) {
        continue
      }

      control.setValidators(this.requiresAddress() ? [requiredValidator] : null)
      control.updateValueAndValidity({ emitEvent: false })
    }
  }

  private applyCustomerValidators(): void {
    const firstNameControl = this.customerFormGroup.controls.first_name
    const lastNameControl = this.customerFormGroup.controls.last_name
    const emailControl = this.customerFormGroup.controls.email
    const isManagedUser = this.isManagedUser()
    const currentUser = this.currentUser()
    const canEditFirstName =
      isManagedUser || !this.normalizeInput(currentUser?.first_name)
    const canEditLastName =
      isManagedUser || !this.normalizeInput(currentUser?.last_name)

    firstNameControl.setValidators(
      canEditFirstName ? [requiredValidator, NameValidator] : [NameValidator]
    )
    if (canEditFirstName) {
      firstNameControl.enable({ emitEvent: false })
    } else {
      firstNameControl.disable({ emitEvent: false })
    }
    firstNameControl.updateValueAndValidity({ emitEvent: false })

    lastNameControl.setValidators(
      canEditLastName ? [requiredValidator, NameValidator] : [NameValidator]
    )
    if (canEditLastName) {
      lastNameControl.enable({ emitEvent: false })
    } else {
      lastNameControl.disable({ emitEvent: false })
    }
    lastNameControl.updateValueAndValidity({ emitEvent: false })

    emailControl.setValidators(
      isManagedUser ? [requiredValidator, emailValidator] : [emailValidator]
    )
    if (isManagedUser) {
      emailControl.enable({ emitEvent: false })
    } else {
      emailControl.disable({ emitEvent: false })
    }
    emailControl.updateValueAndValidity({ emitEvent: false })
  }

  private applyStudentConfirmationValidators(): void {
    const confirmStudentEnrollmentControl =
      this.confirmationFormGroup.controls.confirmStudentEnrollment

    confirmStudentEnrollmentControl.setValidators(
      this.useStudentPricing() ? [requiredTrueValidator] : null
    )
    confirmStudentEnrollmentControl.updateValueAndValidity({ emitEvent: false })
  }

  private patchCustomerFormWithUser(user: UserDto | null | undefined): void {
    if (!user) {
      return
    }

    const controls = this.customerFormGroup.controls

    const patchIfPristine = (
      controlName: keyof typeof controls,
      value: string | null | undefined
    ): void => {
      const control = controls[controlName]
      if (!control.dirty && !control.value) {
        control.patchValue(value ?? null, { emitEvent: false })
      }
    }

    if (!user.managed) {
      patchIfPristine('first_name', user.first_name)
      patchIfPristine('last_name', user.last_name)
      patchIfPristine('email', user.email)
      patchIfPristine('phone_number', user.phone_number)
      patchIfPristine('street', user.street)
      patchIfPristine('house_number', user.house_number)
      patchIfPristine('addition', user.addition)
      patchIfPristine('city', user.city)
      patchIfPristine('province', user.province)
      patchIfPristine('zip_code', user.zip_code)
      patchIfPristine('country', user.country)
    }

    this.syncProvinceStateField(controls.country.value)
  }

  private buildPurchaseOptions(): ArticlePurchaseOptions {
    return {
      useStudentPricing: this.useStudentPricing(),
    }
  }

  private buildCustomerInput(): PurchaseArticleCustomerInput {
    const rawValue = this.customerFormGroup.getRawValue()

    return {
      first_name: this.normalizeInput(rawValue.first_name),
      last_name: this.normalizeInput(rawValue.last_name),
      email: this.normalizeInput(rawValue.email),
      phone_number: this.normalizeInput(rawValue.phone_number),
      street: this.normalizeInput(rawValue.street),
      house_number: this.normalizeInput(rawValue.house_number),
      addition: this.normalizeInput(rawValue.addition),
      city: this.normalizeInput(rawValue.city),
      province: this.normalizeInput(rawValue.province),
      zip_code: this.normalizeInput(rawValue.zip_code),
      country: this.normalizeInput(rawValue.country),
    }
  }

  private normalizeInput(value: string | null | undefined): string | null {
    const normalized = value?.trim()
    return normalized ?? null
  }

  private syncProvinceStateField(country: string | null): void {
    this.selectedCountry.set(country)

    const provinceControl = this.customerFormGroup.controls.province
    const provinceOptions = getProvinceStateFieldOptions(country)
    const currentValue = provinceControl.value

    if (
      provinceOptions.length > 0 &&
      currentValue &&
      !provinceOptions.some(option => option.value === currentValue)
    ) {
      provinceControl.patchValue(null, { emitEvent: false })
    }
  }

  private ensureValidPricingSelection(): void {
    const pricingTypeControl = this.confirmationFormGroup.controls.pricingType
    const selectedPricingType = this.selectedPricingType()

    if (this.studentPricingApplies()) {
      if (
        !this.hasAppliedDefaultPricingSelection() &&
        selectedPricingType !== 'student'
      ) {
        pricingTypeControl.patchValue('student')
        this.hasAppliedDefaultPricingSelection.set(true)
      }
      return
    }

    if (selectedPricingType === 'student' && !this.studentPricingApplies()) {
      pricingTypeControl.patchValue('regular')
    }

    this.hasAppliedDefaultPricingSelection.set(false)
  }

  private getAddressField(
    formControlName: EditableFieldDefinition['formControlName']
  ): EditableFieldDefinition {
    const field = this.addressFields.find(
      addressField => addressField.formControlName === formControlName
    )

    if (!field) {
      throw new Error(`Address field not found: ${formControlName}`)
    }

    return field
  }

  private parseDateString(value: string | null | undefined): Date | null {
    if (!value) {
      return null
    }

    const parsedValue = parseISO(value)
    return Number.isNaN(parsedValue.getTime()) ? null : parsedValue
  }
}
